// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { OcularMotilityPositionResultsType } from "src/services/exams/types";

// Utils
import { getOcularMotilityPositionLabel } from "src/utils/exams/examReportsHelper";
import { OcularMotilityPositionLayoutType } from "src/constants/enums";

// How far off-scale the eye/target gap is drawn -- a raw (x1) plot shows a
// couple of degrees on a plot dozens of degrees wide, so it's deliberately
// exaggerated to actually be visible. Fixed rather than user-adjustable,
// since this is a static report view (see the mockup's own exaggeration
// chips for the interactive version this was adapted from).
const EXAGGERATION = 5;
const GRID_STEP_DEG = 5;
// Pixels per degree of true visual angle. Kept fixed (rather than solved for
// a target canvas size) so the plot's shape is always tightly wrapped around
// whatever the data actually needs -- see below.
const PX_PER_DEG = 16;
// Fixed pixel margins layered on top of the data's own extent, so points and
// spread circles never sit flush against the edge and the position labels
// (centered under each target, sitting below it) have room to render.
const MARGIN_TOP = 24;
const MARGIN_BOTTOM = 46;
const MARGIN_X = 60;

type OcularMotilityFieldPlotType = {
  cardLayout: Array<OcularMotilityPositionLayoutType>;
  positionResults: Array<OcularMotilityPositionResultsType>;
};

const OcularMotilityFieldPlot = ({
  cardLayout,
  positionResults,
}: OcularMotilityFieldPlotType) => {
  const { t } = useTranslation();

  const geometry = useMemo(() => {
    if (positionResults.length === 0) return null;

    // The actual bounding box of what gets drawn
    const exaggerated = positionResults.map((result) => {
      const leftX =
        result.targetAngleX +
        (result.leftEyeAngleX - result.targetAngleX) * EXAGGERATION;
      const leftY =
        result.targetAngleY +
        (result.leftEyeAngleY - result.targetAngleY) * EXAGGERATION;
      const rightX =
        result.targetAngleX +
        (result.rightEyeAngleX - result.targetAngleX) * EXAGGERATION;
      const rightY =
        result.targetAngleY +
        (result.rightEyeAngleY - result.targetAngleY) * EXAGGERATION;
      const leftSpread = result.leftEyeSpread * EXAGGERATION;
      const rightSpread = result.rightEyeSpread * EXAGGERATION;

      return { result, leftX, leftY, leftSpread, rightX, rightY, rightSpread };
    });

    const xExtents = exaggerated.flatMap((position) => [
      position.result.targetAngleX,
      position.leftX - position.leftSpread,
      position.leftX + position.leftSpread,
      position.rightX - position.rightSpread,
      position.rightX + position.rightSpread,
    ]);
    const yExtents = exaggerated.flatMap((position) => [
      position.result.targetAngleY,
      position.leftY - position.leftSpread,
      position.leftY + position.leftSpread,
      position.rightY - position.rightSpread,
      position.rightY + position.rightSpread,
    ]);

    // Always include 0 so the axes stay on-canvas even if every position
    // happens to sit on one side of them.
    const minXDeg = Math.min(0, ...xExtents);
    const maxXDeg = Math.max(0, ...xExtents);
    const minYDeg = Math.min(0, ...yExtents);
    const maxYDeg = Math.max(0, ...yExtents);

    // Rather than scaling data to fit a fixed-size canvas (which leaves a
    // pile of dead space whenever the data's aspect ratio doesn't match the
    // canvas's), size the canvas around the data -- a fixed pixels-per-degree
    // scale plus a fixed label/margin allowance on each side. The four sides
    // are sized independently since the data isn't generally symmetric
    // around the origin.
    const scale = PX_PER_DEG;
    const originX = -minXDeg * scale + MARGIN_X;
    const originY = maxYDeg * scale + MARGIN_TOP;
    const viewWidth = originX + maxXDeg * scale + MARGIN_X;
    const viewHeight = originY + -minYDeg * scale + MARGIN_BOTTOM;

    const toScreen = (angleX: number, angleY: number) => ({
      x: originX + angleX * scale,
      y: originY - angleY * scale,
    });

    const positions = positionResults.map((result) => {
      const target = toScreen(result.targetAngleX, result.targetAngleY);

      return {
        key: result.focalPointPosition,
        label: getOcularMotilityPositionLabel(
          cardLayout,
          result.focalPointPosition
        ),
        target,
        left: {
          x:
            target.x +
            (result.leftEyeAngleX - result.targetAngleX) * scale * EXAGGERATION,
          y:
            target.y -
            (result.leftEyeAngleY - result.targetAngleY) * scale * EXAGGERATION,
          spreadR: result.leftEyeSpread * scale * EXAGGERATION,
        },
        right: {
          x:
            target.x +
            (result.rightEyeAngleX - result.targetAngleX) *
              scale *
              EXAGGERATION,
          y:
            target.y -
            (result.rightEyeAngleY - result.targetAngleY) *
              scale *
              EXAGGERATION,
          spreadR: result.rightEyeSpread * scale * EXAGGERATION,
        },
      };
    });

    const xTicks: Array<number> = [];
    for (
      let deg = GRID_STEP_DEG;
      deg <= Math.floor(maxXDeg / GRID_STEP_DEG) * GRID_STEP_DEG;
      deg += GRID_STEP_DEG
    ) {
      xTicks.push(deg);
    }
    for (
      let deg = GRID_STEP_DEG;
      deg <= Math.floor(-minXDeg / GRID_STEP_DEG) * GRID_STEP_DEG;
      deg += GRID_STEP_DEG
    ) {
      xTicks.push(-deg);
    }
    const yTicks: Array<number> = [];
    for (
      let deg = GRID_STEP_DEG;
      deg <= Math.floor(maxYDeg / GRID_STEP_DEG) * GRID_STEP_DEG;
      deg += GRID_STEP_DEG
    ) {
      yTicks.push(deg);
    }
    for (
      let deg = GRID_STEP_DEG;
      deg <= Math.floor(-minYDeg / GRID_STEP_DEG) * GRID_STEP_DEG;
      deg += GRID_STEP_DEG
    ) {
      yTicks.push(-deg);
    }

    const walkPoints = positions
      .map((position) => `${position.target.x},${position.target.y}`)
      .join(" ");

    return {
      positions,
      xTicks,
      yTicks,
      scale,
      walkPoints,
      originX,
      originY,
      viewWidth,
      viewHeight,
    };
  }, [positionResults, cardLayout]);

  if (!geometry) return null;

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography fontSize="12px" color="#5B6D7A">
        {t(
          "Deviations between each eye and its target are exaggerated ×{{factor}} to be visible. The targets and the order they were walked in are drawn true to scale.",
          { factor: EXAGGERATION }
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          fontSize: "12px",
        }}
      >
        <Box display="flex" alignItems="center" gap="8px">
          <Box
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: "red",
            }}
          />
          {t("Left Eye")}
        </Box>
        <Box display="flex" alignItems="center" gap="8px">
          <Box
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: "blue",
            }}
          />
          {t("Right Eye")}
        </Box>
        <Box display="flex" alignItems="center" gap="8px">
          <svg
            width="12"
            height="12"
            viewBox="-7 -7 14 14"
            aria-hidden="true"
            style={{ display: "block", flexShrink: 0 }}
          >
            <path d="M-7 0h14M0 -7v14" stroke="#5B6D7A" strokeWidth={1.5} />
          </svg>
          {t("Focal Point Target")}
        </Box>
        <Box display="flex" alignItems="center" gap="8px">
          <svg
            width="16"
            height="12"
            aria-hidden="true"
            style={{ display: "block", flexShrink: 0 }}
          >
            {/* 16px is an exact multiple of the "4 4" dash+gap period, so
                the pattern ends cleanly instead of getting cut off mid-dash. */}
            <line
              x1={0}
              y1={6}
              x2={16}
              y2={6}
              stroke="#15803D"
              strokeWidth={2}
              strokeDasharray="4 4"
              opacity={0.8}
            />
          </svg>
          {t("Order of Positions")}
        </Box>
      </Box>

      <svg
        viewBox={`0 0 ${geometry.viewWidth} ${geometry.viewHeight}`}
        role="img"
        aria-label={t("All nine positions on one plot")}
        style={{ width: "100%", height: "auto" }}
      >
        {geometry.xTicks.map((deg) => (
          <line
            key={`x-${deg}`}
            x1={geometry.originX + deg * geometry.scale}
            y1={0}
            x2={geometry.originX + deg * geometry.scale}
            y2={geometry.viewHeight}
            stroke="#5B6D7A20"
          />
        ))}
        {geometry.yTicks.map((deg) => (
          <line
            key={`y-${deg}`}
            x1={0}
            y1={geometry.originY - deg * geometry.scale}
            x2={geometry.viewWidth}
            y2={geometry.originY - deg * geometry.scale}
            stroke="#5B6D7A20"
          />
        ))}
        <line
          x1={0}
          y1={geometry.originY}
          x2={geometry.viewWidth}
          y2={geometry.originY}
          stroke="#5B6D7A60"
        />
        <line
          x1={geometry.originX}
          y1={0}
          x2={geometry.originX}
          y2={geometry.viewHeight}
          stroke="#5B6D7A60"
        />

        <polyline
          points={geometry.walkPoints}
          fill="none"
          stroke="#15803D"
          strokeWidth={2}
          strokeDasharray="4 4"
          opacity={0.8}
        />

        {geometry.positions.map((position) => (
          <g key={position.key}>
            <path
              d={`M${position.target.x - 7} ${position.target.y}h14M${position.target.x} ${position.target.y - 7}v14`}
              stroke="#5B6D7A"
              strokeWidth={1.5}
            />
            <text
              x={position.target.x}
              y={position.target.y + 26}
              fontSize="16"
              textAnchor="middle"
              fill="#5B6D7A"
              stroke="#FFF"
              strokeWidth={3}
              paintOrder="stroke"
            >
              {t(position.label)}
            </text>

            <line
              x1={position.target.x}
              y1={position.target.y}
              x2={position.left.x}
              y2={position.left.y}
              stroke="red"
              strokeWidth={1.25}
              opacity={0.85}
            />
            <circle
              cx={position.left.x}
              cy={position.left.y}
              r={position.left.spreadR}
              fill="none"
              stroke="red"
              opacity={0.4}
            />
            <circle
              cx={position.left.x}
              cy={position.left.y}
              r={5}
              fill="red"
            />

            <line
              x1={position.target.x}
              y1={position.target.y}
              x2={position.right.x}
              y2={position.right.y}
              stroke="blue"
              strokeWidth={1.25}
              opacity={0.85}
            />
            <circle
              cx={position.right.x}
              cy={position.right.y}
              r={position.right.spreadR}
              fill="none"
              stroke="blue"
              opacity={0.4}
            />
            <circle
              cx={position.right.x}
              cy={position.right.y}
              r={5}
              fill="blue"
            />
          </g>
        ))}
      </svg>
    </Box>
  );
};

export default OcularMotilityFieldPlot;
